import { CHART_COLORS } from '@/constants/chartColors'
import { formatCurrency, formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Download } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { Button, Typogaphy } from 'vnetwork-ui'

const COLORS = [
    CHART_COLORS.PRIMARY_BLUE,
    CHART_COLORS.EMERALD_GREEN,
    CHART_COLORS.AMBER_ORANGE,
    CHART_COLORS.INDIGO_PURPLE,
    CHART_COLORS.PINK_FUCHSIA,
    CHART_COLORS.BLUE_GRAY,
    CHART_COLORS.ROSE_RED,
]

interface RevenueShareData {
    name: string
    value: number
    share: number
    [key: string]: string | number
}

interface RevenueShareChartProps {
    data: RevenueShareData[]
    comparisonData?: RevenueShareData[]
    currentPeriodLabel?: string
    comparePeriodLabel?: string
    hasComparison?: boolean
}

const RevenueShareChart = ({
    data,
    comparisonData = [],
    currentPeriodLabel,
    comparePeriodLabel,
    hasComparison = false,
}: RevenueShareChartProps) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)

    const exportChartToPDF = async () => {
        if (!chartRef.current) return

        try {
            const canvas = await html2canvas(chartRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
            })

            const imgData = canvas.toDataURL('image/png')
            const pdf = new jsPDF('l', 'mm', 'a4')
            const pdfWidth = pdf.internal.pageSize.getWidth()
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width

            pdf.addImage(imgData, 'PNG', 0, 10, pdfWidth, pdfHeight)
            pdf.save(`revenue-share-${dayjs().format('YYYY-MM-DD')}.pdf`)
        } catch (error) {
            console.error('Export PDF error:', error)
        }
    }

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                    <p className="font-medium">{payload[0].name}</p>
                    <p className="text-sm text-gray-600">
                        {formatCurrency(payload[0].value)}
                    </p>
                    {payload[0].payload.share && (
                        <p className="text-sm text-gray-600">
                            Tỷ trọng: {formatNumeral(payload[0].payload.share)}%
                        </p>
                    )}
                </div>
            )
        }
        return null
    }

    return (
        <div className="space-y-2" ref={chartRef}>
            <div className="flex items-center justify-between">
                <Typogaphy size="small" className="font-medium text-gray-700">
                    {t('employees.performance.revenue_share_by_service')}
                </Typogaphy>
                <Button
                    size="small"
                    variant="text"
                    onClick={exportChartToPDF}
                    className="!p-1"
                >
                    <Download className="w-4 h-4" />
                </Button>
            </div>

            <div
                className={`grid gap-4 ${hasComparison ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}
            >
                {/* Current Period Donut */}
                <div className="space-y-2">
                    {hasComparison && (
                        <Typogaphy
                            size="small"
                            className="text-center text-gray-600 font-medium"
                        >
                            {currentPeriodLabel}
                        </Typogaphy>
                    )}
                    <div className="flex items-center justify-center">
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={data}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={80}
                                    paddingAngle={2}
                                >
                                    {data.map((_, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Comparison Period Donut */}
                {hasComparison && comparisonData.length > 0 && (
                    <div className="space-y-2">
                        <Typogaphy
                            size="small"
                            className="text-center text-gray-500 font-medium"
                        >
                            {comparePeriodLabel}
                        </Typogaphy>
                        <div className="flex items-center justify-center">
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={comparisonData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                    >
                                        {comparisonData.map((_, index) => (
                                            <Cell
                                                key={`cell-comp-${index}`}
                                                fill={
                                                    COLORS[
                                                        index % COLORS.length
                                                    ]
                                                }
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>

            {/* Shared Legend */}
            <div className="flex flex-wrap justify-center gap-3 mt-2">
                {data.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-1.5">
                        <div
                            className="w-3 h-3 rounded-full"
                            style={{
                                backgroundColor: COLORS[index % COLORS.length],
                            }}
                        />
                        <span className="text-xs text-gray-600">
                            {item.name}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default RevenueShareChart
